// BulkImportModal — paste-CSV → preview-per-row → commit pattern.
//
// One component, two kinds (chits | payments) — keeps the upload UX identical
// while letting each kind document its own column list and call its own proc.

import { useState, type ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

type Kind = 'chits' | 'payments';

interface RowResult {
  row_index: number;
  ok: boolean;
  error: string | null;
  data?: { chit_id?: string; subscription_id?: string; was_idempotent?: boolean } | null;
}

interface BulkResult {
  results: RowResult[];
  summary: Record<string, number>;
}

interface Props {
  kind: Kind;
  on_close: () => void;
  on_done?: () => void;
}

const TEMPLATES: Record<Kind, { header: string; example: string; required_cols: string[] }> = {
  chits: {
    header: 'name_ta,name_en,pool_size,duration_months,monthly_subscription_paise,collection_day,start_date,foreman_commission_pct',
    example:
      'ஜனவரி கூட்டம்,January Group,20,20,5000000,10,2026-01-01,5\nஏப்ரல் கூட்டம்,April Group,15,15,1000000,15,2026-04-01,5',
    required_cols: [
      'name_ta',
      'name_en',
      'pool_size',
      'duration_months',
      'monthly_subscription_paise',
      'collection_day',
      'start_date',
    ],
  },
  payments: {
    header: 'chit,subscriber_phone,due_month,amount_paise,method,reference,note',
    example:
      'January Group,+919876543210,2026-05,5000000,upi,UPI123,\nApril Group,+919876511111,2026-05,1000000,cash,,',
    required_cols: ['chit', 'subscriber_phone', 'due_month', 'amount_paise', 'method'],
  },
};

export function BulkImportModal({ kind, on_close, on_done }: Props): ReactElement {
  const { t } = useTranslation();
  const [csv_text, set_csv_text] = useState('');
  const [result, set_result] = useState<BulkResult | null>(null);
  const tpl = TEMPLATES[kind];

  const m = useMutation({
    mutationFn: async () => {
      if (kind === 'chits') {
        return orpc.bulk.chits.bulk_create({ csv_text }) as unknown as Promise<BulkResult>;
      }
      return orpc.bulk.payments.bulk_mark_paid({ csv_text }) as unknown as Promise<BulkResult>;
    },
    onSuccess: (r) => {
      set_result(r);
      on_done?.();
    },
  });

  const total = result?.results.length ?? 0;
  const ok_count = result?.results.filter((r) => r.ok).length ?? 0;
  const failed = total - ok_count;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm">
      <div className="bg-surface border border-line rounded-lg w-full max-w-3xl p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-medium mb-1">
          {kind === 'chits'
            ? t('bulk.chits_title', 'Bulk import chits')
            : t('bulk.payments_title', 'Bulk record payments')}
        </h2>
        <p className="text-xs text-ink/60 mb-3">
          {t('bulk.hint', 'Paste a CSV. First row must be the header.')}
        </p>

        <div className="mb-3 p-3 bg-bg-elev rounded text-[11px] font-mono break-all">
          <div className="text-ink/60 mb-1">{t('bulk.header', 'Header')}:</div>
          <div>{tpl.header}</div>
        </div>

        <textarea
          rows={10}
          value={csv_text}
          onChange={(e) => set_csv_text(e.target.value)}
          placeholder={`${tpl.header}\n${tpl.example}`}
          className="w-full px-3 py-2 border border-line rounded text-xs font-mono mb-3"
        />

        {m.error && (
          <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded mb-3">
            {friendly_error(m.error)}
          </div>
        )}

        {result && (
          <div className="mb-3">
            <div className="flex items-baseline gap-3 text-sm mb-2">
              <span className="font-medium">
                {ok_count}/{total} {t('bulk.ok', 'ok')}
              </span>
              {failed > 0 && <span className="text-bad">{failed} {t('bulk.failed', 'failed')}</span>}
              {(result.summary.idempotent_skipped ?? 0) > 0 && (
                <span className="text-ink/60">
                  {result.summary.idempotent_skipped} {t('bulk.skipped', 'duplicate-skipped')}
                </span>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto border border-line rounded">
              <table className="w-full text-xs">
                <thead className="bg-bg-elev border-b border-line text-ink/60">
                  <tr>
                    <th className="text-left px-3 py-1">#</th>
                    <th className="text-left px-3 py-1">Status</th>
                    <th className="text-left px-3 py-1">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((row) => (
                    <tr key={row.row_index} className="border-b border-line/40 last:border-b-0">
                      <td className="px-3 py-1 font-mono">{row.row_index + 1}</td>
                      <td className="px-3 py-1">
                        {row.ok ? (
                          <span className="text-good">
                            ✓ {row.data?.was_idempotent ? 'skipped (dup)' : 'ok'}
                          </span>
                        ) : (
                          <span className="text-bad">✗ failed</span>
                        )}
                      </td>
                      <td className="px-3 py-1 text-ink/70 font-mono break-all">
                        {row.error ?? row.data?.chit_id ?? row.data?.subscription_id ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={on_close}
            className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
          >
            {result ? t('actions.close', 'Close') : t('actions.cancel', 'Cancel')}
          </button>
          {!result && (
            <button
              type="button"
              disabled={m.isPending || csv_text.trim().length === 0}
              onClick={() => m.mutate()}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
            >
              {m.isPending ? '…' : t('bulk.commit', 'Import')}
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={() => {
                set_result(null);
                set_csv_text('');
              }}
              className="border border-line px-4 py-2 rounded text-sm text-ink/70 hover:bg-bg-elev"
            >
              {t('bulk.another', 'Import more')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
